import { useId, type FormEvent } from 'react';
import { Alert, Button, Form, Label, Modal, Select } from '@shared/components';
import { DOCUMENT_TYPE_LABELS } from '@/features/document-templates/utils/documentTypeLabels';
import { ASSIGNABLE_DOCUMENT_TYPES, type AssignableDocumentType } from '@/features/document-templates/types/documentTemplates.types';

interface Props {
    isOpen: boolean;
    documentType: AssignableDocumentType | '';
    fieldError?: string;
    formError?: string;
    isSubmitting: boolean;
    onDocumentTypeChange: (documentType: AssignableDocumentType) => void;
    onSubmit: () => void;
    onClose: () => void;
}

const DOCUMENT_TYPE_OPTIONS = ASSIGNABLE_DOCUMENT_TYPES.map((value) => ({ value, label: DOCUMENT_TYPE_LABELS[value] }));

export function GenerateDocumentModal({ isOpen, documentType, fieldError, formError, isSubmitting, onDocumentTypeChange, onSubmit, onClose }: Props) {
    const headingId = useId();
    const fieldErrorId = useId();
    const submit = (event: FormEvent) => {
        event.preventDefault();
        onSubmit();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} ariaLabelledBy={headingId} className="w-full max-w-lg p-6">
            <h2 id={headingId} className="mb-2 text-lg font-semibold">Generate document</h2>
            <p className="mb-4 text-sm text-mp-muted">
                The document uses the template assigned to this agreement's product. It appears in the list straight away and
                is ready in a few seconds.
            </p>
            <Form className="space-y-4" onSubmit={submit}>
                {formError && <Alert variant="error">{formError}</Alert>}
                <div>
                    <Label id="generate-document-type-label" htmlFor="generate-document-type" required>Document</Label>
                    <Select
                        id="generate-document-type"
                        ariaLabelledBy="generate-document-type-label"
                        ariaDescribedBy={fieldError ? fieldErrorId : undefined}
                        options={DOCUMENT_TYPE_OPTIONS}
                        placeholder="Choose a document"
                        value={documentType}
                        error={Boolean(fieldError)}
                        disabled={isSubmitting}
                        onChange={(value) => onDocumentTypeChange(value as AssignableDocumentType)}
                    />
                    {fieldError && (
                        <p id={fieldErrorId} role="alert" className="mt-1.5 text-xs text-mp-error">
                            {fieldError}
                        </p>
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="neutral" appearance="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Generating...' : 'Generate'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}
